import { Controller } from "stimulus"

// Menu Scheduling (EM-2356).
//
// The editor holds a draft of one service in memory and saves it in one go, so
// day groups can be added, split and cancelled without touching the server. The
// day-group markup is built here rather than server-rendered: titles, overnight
// pills, warnings and the span bar all move as the operator types.
//
// The derivations below deliberately mirror V2::MenuSchedulingPresenter. The
// server is still the authority -- it recomputes all of this when it renders the
// page after a save -- but the two need to agree while the modal is open.
const WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
const MINUTES_IN_DAY = 1440

export default class extends Controller {
  static targets = [
    "modal", "modalTitle", "modalMenuName", "groups", "notice", "error", "summary", "saveButton",
    "typeSection", "typeHeading", "typeOptions", "typeHint",
    "daysHeading", "floorsSection", "floorsHeading", "floorOptions",
    "availHeading", "channelOptions", "payHeading", "paymentOptions",
    "snoozeModal", "snoozeTitle", "snoozeSubject", "snoozeOptions", "snoozeCustom", "snoozeConfirm",
    "snoozeDate", "snoozeTime", "snoozeError", "snoozeQuestion", "snoozeConfirmButton",
    "toast", "openingToday", "dot", "snoozeButton", "chipNote",
  ]

  static values = { payload: Object }

  connect() {
    this.data_ = this.payloadValue
    this.draft = null
    this.snooze = null
    // The dots say whether a schedule is serving *now*, so they go stale on
    // their own. Re-rendering on a timer keeps them honest without a reload.
    this.ticker = setInterval(() => this.refreshStatuses(), 20000)
  }

  disconnect() {
    clearInterval(this.ticker)
    clearTimeout(this.toastTimer)
  }

  // ---- opening hours ----

  openingFor(day) {
    const hours = this.data_.opening_hours[day]
    if (!hours) return null
    return hours
  }

  // Days grouped by identical venue hours, with the closed ones set aside. This
  // is what splits a mixed selection into one block per set of hours.
  openingBlocks(days) {
    const map = {}
    const order = []
    const closed = []

    this.sortedDays(days).forEach(day => {
      const hours = this.openingFor(day)
      if (!hours) { closed.push(day); return }
      const key = `${hours.open}|${hours.close}`
      if (!map[key]) { map[key] = { days: [], open: hours.open, close: hours.close }; order.push(key) }
      map[key].days.push(day)
    })

    return { blocks: order.map(key => map[key]), closed }
  }

  openingText(days) {
    if (!days.length) return "Pick days to see venue hours"
    const result = this.openingBlocks(days)
    if (!result.blocks.length) return "Venue closed on the days you picked"
    if (result.blocks.length === 1) return `Venue open ${result.blocks[0].open} – ${result.blocks[0].close}`
    return `Venue open · ${result.blocks.length} different opening times`
  }

  matchesOpening(block) {
    const result = this.openingBlocks(block.days)
    if (result.blocks.length !== 1 || result.closed.length) return false
    return result.blocks[0].open === block.vs && result.blocks[0].close === block.ve
  }

  // ---- time helpers ----

  mins(value) {
    const parts = String(value || "00:00").split(":")
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0)
  }

  // Equal start and end stays a zero-length block, matching the server.
  isOvernight(from, to) {
    if (!from || !to) return false
    return this.mins(to) < this.mins(from)
  }

  duration(from, to) {
    if (!from || !to) return 0
    const span = this.mins(to) - this.mins(from)
    return span < 0 ? span + MINUTES_IN_DAY : span
  }

  durationText(minutes) {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    if (hours && rest) return `${hours}h ${rest}m`
    if (hours) return `${hours}h`
    return `${rest} min`
  }

  timeText(from, to) {
    if (!from || !to) return "Not set"
    return `${from} – ${to}${this.isOvernight(from, to) ? " (+1)" : ""}`
  }

  sortedDays(days) {
    return WEEK.filter(day => (days || []).indexOf(day) >= 0)
  }

  contiguous(days) {
    const indexes = days.map(day => WEEK.indexOf(day))
    for (let i = 1; i < indexes.length; i++) if (indexes[i] !== indexes[i - 1] + 1) return false
    return indexes.length > 0
  }

  groupTitle(days) {
    const ordered = this.sortedDays(days)
    if (!ordered.length) return "No days selected"
    if (ordered.length === WEEK.length) return "Every day"
    if (ordered.length === 1) return ordered[0]
    if (this.contiguous(ordered)) return `${ordered[0]} to ${ordered[ordered.length - 1]}`
    return ordered.join(", ")
  }

  groupShort(days) {
    const ordered = this.sortedDays(days)
    if (!ordered.length) return "—"
    if (ordered.length === WEEK.length) return "Daily"
    if (ordered.length === 1) return ordered[0].slice(0, 3)
    if (this.contiguous(ordered)) return `${ordered[0].slice(0, 3)}–${ordered[ordered.length - 1].slice(0, 3)}`
    return ordered.map(day => day.slice(0, 3)).join(", ")
  }

  nextMorning(days) {
    const ordered = this.sortedDays(days)
    if (!ordered.length) return "the next morning"
    const after = day => WEEK[(WEEK.indexOf(day) + 1) % WEEK.length]
    if (ordered.length === 1) return `${after(ordered[0])} morning`
    if (ordered.length === WEEK.length || !this.contiguous(ordered)) return "the next morning"
    return `${after(ordered[0]).slice(0, 3)}–${after(ordered[ordered.length - 1]).slice(0, 3)} mornings`
  }

  // Both windows measured from the moment the menu becomes visible, so the
  // comparisons survive midnight.
  facts(block) {
    const os = block.match ? block.vs : block.os
    const oe = block.match ? block.ve : block.oe
    const visLen = this.duration(block.vs, block.ve)
    const ordLen = this.duration(os, oe)
    const offset = (this.mins(os) - this.mins(block.vs) + MINUTES_IN_DAY) % MINUTES_IN_DAY

    return {
      os, oe,
      visOvernight: this.isOvernight(block.vs, block.ve),
      ordOvernight: this.isOvernight(os, oe),
      visLen, ordLen, offset,
      earlyBy: visLen - (offset + ordLen),
    }
  }

  warningFor(block, facts) {
    if (!block.vs || !block.ve || !facts.os || !facts.oe) return ""
    if (facts.earlyBy < 0) return "Ordering closes after the menu stops being visible — orders could land after service ends."
    if (facts.offset > 0 && facts.offset > facts.visLen) return "Ordering opens before the menu is visible."
    return ""
  }

  // ---- opening the editor ----

  openEdit(event) {
    const menuId = Number(event.currentTarget.dataset.menuId)
    const serviceTypeId = Number(event.currentTarget.dataset.serviceTypeId)
    const menu = this.data_.menus.find(item => item.id === menuId)
    const service = menu.services.find(item => item.service_type_id === serviceTypeId)

    this.draft = {
      mode: "edit",
      menuId,
      editingId: null,
      snapshot: null,
      error: "",
      notice: "",
      service: JSON.parse(JSON.stringify(service)),
    }
    this.renderModal()
  }

  openAdd(event) {
    const menuId = Number(event.currentTarget.dataset.menuId)
    const menu = this.data_.menus.find(item => item.id === menuId)
    const used = menu.services.map(item => item.service_type_id)
    const available = this.data_.service_types.filter(type => used.indexOf(type.id) < 0)
    const first = this.blankBlock()

    this.draft = {
      mode: "add",
      menuId,
      editingId: this.blockKey(first),
      snapshot: null,
      error: "",
      notice: "",
      service: {
        service_type_id: available.length === 1 ? available[0].id : null,
        online: true,
        pos: false,
        payment_method_ids: [],
        floor_ids: [],
        schedules: [first],
      },
    }
    this.renderModal()
  }

  blankBlock(seed) {
    return {
      id: null,
      key: `new-${Math.random().toString(36).slice(2, 8)}`,
      days: [],
      vs: seed ? seed.vs : "",
      ve: seed ? seed.ve : "",
      os: seed ? seed.vs : "",
      oe: seed ? seed.ve : "",
      match: true,
      status: "active",
    }
  }

  closeModal() {
    this.draft = null
    this.modalTarget.hidden = true
  }

  blockKey(block) {
    return block.key || `id-${block.id}`
  }

  findBlock(key) {
    return this.draft.service.schedules.find(block => this.blockKey(block) === key)
  }

  // ---- day group actions ----

  editGroup(event) {
    const block = this.findBlock(event.currentTarget.dataset.key)
    this.draft.snapshot = JSON.parse(JSON.stringify(block))
    this.draft.editingId = this.blockKey(block)
    this.draft.error = ""
    this.renderModal()
  }

  // Cancelling a block that was never saved removes it; otherwise the pre-edit
  // snapshot comes back.
  cancelGroup(event) {
    const key = event.currentTarget.dataset.key
    const schedules = this.draft.service.schedules
    const index = schedules.findIndex(block => this.blockKey(block) === key)

    if (this.draft.snapshot && this.blockKey(this.draft.snapshot) === key) {
      schedules[index] = this.draft.snapshot
    } else if (index >= 0) {
      schedules.splice(index, 1)
    }

    this.draft.snapshot = null
    this.draft.editingId = null
    this.draft.error = ""
    this.renderModal()
  }

  doneGroup(event) {
    const block = this.findBlock(event.currentTarget.dataset.key)

    if (!block.days.length) return this.fail("Pick at least one day for this schedule.")
    if (!block.vs || !block.ve) return this.fail("Set the menu visible times for this schedule.")
    if (block.match) { block.os = block.vs; block.oe = block.ve }
    if (!block.os || !block.oe) return this.fail("Set the ordering window, or tick Match viewing hours.")

    this.draft.snapshot = null
    this.draft.editingId = null
    this.draft.error = ""
    this.renderModal()
  }

  removeGroup(event) {
    const key = event.currentTarget.dataset.key
    this.draft.service.schedules = this.draft.service.schedules.filter(block => this.blockKey(block) !== key)
    if (this.draft.editingId === key) { this.draft.editingId = null; this.draft.snapshot = null }
    this.renderModal()
  }

  addSchedule() {
    const schedules = this.draft.service.schedules
    const block = this.blankBlock(schedules[schedules.length - 1])
    schedules.push(block)
    this.draft.snapshot = null
    this.draft.editingId = this.blockKey(block)
    this.draft.error = ""
    this.draft.notice = ""
    this.renderModal()
  }

  toggleDay(event) {
    const block = this.findBlock(event.currentTarget.dataset.key)
    const day = event.currentTarget.dataset.day
    const index = block.days.indexOf(day)
    if (index >= 0) block.days.splice(index, 1); else block.days.push(day)
    block.days = this.sortedDays(block.days)
    this.renderModal()
  }

  everyDay(event) {
    this.findBlock(event.currentTarget.dataset.key).days = WEEK.slice()
    this.renderModal()
  }

  clearDays(event) {
    this.findBlock(event.currentTarget.dataset.key).days = []
    this.renderModal()
  }

  // Ticking it discards the ordering edits and mirrors the viewing hours again.
  toggleMatch(event) {
    const block = this.findBlock(event.currentTarget.dataset.key)
    block.match = !block.match
    if (block.match) { block.os = block.vs; block.oe = block.ve }
    this.renderModal()
  }

  setTime(event) {
    const block = this.findBlock(event.currentTarget.dataset.key)
    block[event.currentTarget.dataset.field] = event.currentTarget.value
    // Mirroring happens as they type, not on blur.
    if (block.match && ["vs", "ve"].indexOf(event.currentTarget.dataset.field) >= 0) {
      block.os = block.vs
      block.oe = block.ve
    }
    this.draft.error = ""
    this.draft.notice = ""
    this.renderModal()
  }

  // Sets both windows to the venue hours, splitting the block when the selected
  // days do not share them and dropping the days the venue is shut.
  useOpeningHours(event) {
    const key = event.currentTarget.dataset.key
    const block = this.findBlock(key)

    if (!block.days.length) return this.fail("Pick the days first, then align to opening hours.")

    const result = this.openingBlocks(block.days)
    if (!result.blocks.length) return this.fail("The restaurant is closed on every day you picked.")

    const made = result.blocks.map((group, index) => {
      const copy = Object.assign({}, block, {
        days: group.days.slice(),
        vs: group.open, ve: group.close, os: group.open, oe: group.close,
      })
      if (index > 0) { copy.id = null; copy.key = `new-${Math.random().toString(36).slice(2, 8)}` }
      return copy
    })

    const schedules = this.draft.service.schedules
    schedules.splice(schedules.findIndex(item => this.blockKey(item) === key), 1, ...made)

    const notes = []
    if (made.length > 1) notes.push(`Split into ${made.length} blocks — those days have different opening hours.`)
    if (result.closed.length) notes.push(`Dropped ${result.closed.map(day => day.slice(0, 3)).join(", ")} — the venue is closed then.`)
    if (!block.match) notes.push("Ordering window aligned to the opening hours too — adjust it if orders should stop earlier.")

    this.draft.snapshot = null
    this.draft.editingId = made.length === 1 ? this.blockKey(made[0]) : null
    this.draft.error = ""
    this.draft.notice = notes.join(" ")
    this.renderModal()
  }

  // The fast path for first-time setup: a whole week from the venue's hours.
  alignAll() {
    const result = this.openingBlocks(WEEK)
    if (!result.blocks.length) return this.fail("The restaurant has no opening hours set.")

    this.draft.service.schedules = result.blocks.map(group => {
      const block = this.blankBlock()
      block.days = group.days.slice()
      block.vs = group.open
      block.ve = group.close
      block.os = group.open
      block.oe = group.close
      return block
    })

    this.draft.editingId = null
    this.draft.snapshot = null
    this.draft.error = ""
    this.draft.notice = `Rebuilt ${this.draft.service.schedules.length} blocks from the venue opening hours.`
    this.renderModal()
  }

  fail(message) {
    this.draft.error = message
    this.renderModal()
  }

  // ---- service-level choices ----

  pickType(event) {
    this.draft.service.service_type_id = Number(event.currentTarget.dataset.id)
    this.draft.error = ""
    this.renderModal()
  }

  toggleChannel(event) {
    const field = event.currentTarget.dataset.field
    this.draft.service[field] = !this.draft.service[field]
    this.draft.error = ""
    this.renderModal()
  }

  pickPayment(event) {
    this.draft.service.payment_method_ids = [Number(event.currentTarget.dataset.id)]
    this.draft.error = ""
    this.renderModal()
  }

  toggleFloor(event) {
    const id = Number(event.currentTarget.dataset.id)
    const floors = this.draft.service.floor_ids
    const index = floors.indexOf(id)
    if (index >= 0) floors.splice(index, 1); else floors.push(id)
    this.renderModal()
  }

  // ---- saving ----

  save() {
    const service = this.draft.service
    const blocks = service.schedules

    let error = ""
    if (this.draft.mode === "add" && !service.service_type_id) error = "Choose a service type."
    else if (!blocks.length) error = "Add at least one schedule."
    else if (blocks.some(block => !block.days.length)) error = "Every schedule needs at least one day."
    else if (blocks.some(block => !block.vs || !block.ve)) error = "Every schedule needs menu visible times."
    else if (!service.online && !service.pos) error = "Choose at least one channel under Available On."
    else if (!service.payment_method_ids.length) error = "Payment type is required."
    // The server refuses this combination too, so it is caught before the round
    // trip rather than coming back as a validation error.
    else {
      const broken = blocks.find(block => this.warningFor(block, this.facts(block)))
      if (broken) error = this.warningFor(broken, this.facts(broken))
    }

    if (error) return this.fail(error)

    const menu = this.data_.menus.find(item => item.id === this.draft.menuId)
    const others = menu.services.filter(item => item.service_type_id !== service.service_type_id)
    const payload = others.concat([service])

    this.submit(menu, payload)
  }

  // The whole menu goes up each time: the updater owns every service type on the
  // menu at once, so sending one service alone would retire the others.
  submit(menu, services) {
    const body = {
      menu_schedule_editor: {
        service_type_ids: services.map(service => service.service_type_id).filter(Boolean),
        blocks: services.flatMap(service => service.schedules.map(block => ({
          id: block.id,
          service_type_id: service.service_type_id,
          days: block.days,
          view_start: block.vs,
          view_end: block.ve,
          match_viewing: block.match ? "1" : "0",
          order_start: block.os,
          order_end: block.oe,
          online: service.online ? "1" : "0",
          pos: service.pos ? "1" : "0",
          payment_method_ids: service.payment_method_ids,
          floor_ids: service.floor_ids,
        }))),
      },
    }

    this.saveButtonTarget.disabled = true

    fetch(this.data_.update_url_template.replace(":menu_id", menu.id), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify(body),
    })
      .then(response => response.json().then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) { this.saveButtonTarget.disabled = false; return this.fail(data.error || "Failed to save schedule.") }
        this.showToast(this.draft.mode === "add" ? "Service type added" : "Schedule saved")
        window.location.reload()
      })
      .catch(() => {
        this.saveButtonTarget.disabled = false
        this.fail("Failed to save schedule.")
      })
  }

  // ---- snooze ----

  openSnooze(event) {
    const id = Number(event.currentTarget.dataset.scheduleId)
    const block = this.blockById(id)
    const status = block ? block.status : "active"

    this.snooze = {
      id,
      status,
      subject: block ? `${this.groupTitle(block.days)} · ${this.timeText(block.vs, block.ve)}` : "",
      view: status === "active" || status === "live" ? "options" : "confirm",
      date: this.venueTomorrow(),
      time: "09:00",
      error: "",
    }
    this.renderSnooze()
  }

  blockById(id) {
    for (const menu of this.data_.menus) {
      for (const service of menu.services) {
        const found = service.schedules.find(block => block.id === id)
        if (found) return found
      }
    }
    return null
  }

  closeSnooze() {
    this.snooze = null
    this.snoozeModalTarget.hidden = true
  }

  snoozeOptionsView() { this.snooze.view = "options"; this.snooze.error = ""; this.renderSnooze() }
  snoozeCustomView() { this.snooze.view = "custom"; this.snooze.error = ""; this.renderSnooze() }

  snoozeDay() { this.sendSnooze({ period: "day" }) }
  snoozeWeek() { this.sendSnooze({ period: "week" }) }
  deactivate() { this.sendSnooze({ period: "permanent" }) }

  // The time is sent as the operator typed it, with no zone, so the server
  // reads it in the venue's zone. Sending an ISO instant instead made a 09:00
  // snooze come back at 10:00 through British Summer Time.
  snoozeCustomApply() {
    const date = this.snoozeDateTarget.value
    const time = this.snoozeTimeTarget.value || "09:00"
    const instant = this.venueInstant(date, time)

    if (!instant || Number.isNaN(instant) || instant <= Date.now()) {
      this.snooze.error = "Pick a date and time in the future."
      return this.renderSnooze()
    }

    this.sendSnooze({ period: "custom", return_at: `${date}T${time}` })
  }

  resume() {
    this.sendSnooze(null)
  }

  // Pausing a schedule must not cost the operator a half-finished draft, so the
  // new state is folded into what is already on screen rather than reloaded.
  // The block is patched wherever it appears: the payload, the index row, and
  // any open edit draft.
  applyStatus(data) {
    const block = this.blockById(data.id)
    if (block) {
      block.status = data.status
      block.snoozed_until = data.snoozed_until
      block.snoozed_until_label = data.snoozed_until_label
    }

    const draftBlock = this.draft && this.draft.service.schedules.find(item => item.id === data.id)
    if (draftBlock) {
      draftBlock.status = data.status
      draftBlock.snoozed_until = data.snoozed_until
      draftBlock.snoozed_until_label = data.snoozed_until_label
    }

    this.paintIndexStatus(data)
    this.showToast(this.statusToast(data.status))
    if (this.draft) this.renderModal()
  }

  paintIndexStatus(data) {
    const muted = data.status === "snoozed" || data.status === "inactive"

    this.dotTargets
      .filter(dot => Number(dot.dataset.scheduleId) === data.id)
      .forEach(dot => {
        dot.className = `menu-scheduling-dot is-${data.status}`
        dot.title = data.snoozed_until_label || (muted ? "Deactivated" : "Active")
      })

    this.snoozeButtonTargets
      .filter(button => Number(button.dataset.scheduleId) === data.id)
      .forEach(button => { button.textContent = this.labelForStatus(data.status) })

    this.chipNoteTargets
      .filter(note => Number(note.dataset.scheduleId) === data.id)
      .forEach(note => {
        note.textContent = data.snoozed_until_label || "Deactivated"
        note.hidden = !muted
      })
  }

  labelForStatus(status) {
    if (status === "snoozed") return "Un-Snooze"
    if (status === "inactive") return "Reactivate"
    return "Snooze"
  }

  statusToast(status) {
    if (status === "snoozed") return "Schedule snoozed"
    if (status === "inactive") return "Schedule deactivated"
    return "Schedule serving again"
  }

  sendSnooze(params) {
    const url = this.data_.snooze_url_template.replace(":id", this.snooze.id)

    fetch(url, {
      method: params ? "POST" : "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: params ? JSON.stringify(params) : null,
    })
      .then(response => response.json().then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) { this.snooze.error = data.error || "Could not change this schedule."; return this.renderSnooze() }
        this.applyStatus(data)
        this.closeSnooze()
      })
      .catch(() => {
        this.snooze.error = "Could not change this schedule."
        this.renderSnooze()
      })
  }

  // ---- rendering ----

  // "Live right now" is a statement about the clock, so it stops being true on
  // its own. The dots and the modal's pills are recomputed on a timer rather
  // than waiting for a reload.
  //
  // Only the live/active half is worked out here: whether a schedule is snoozed
  // or switched off changes when someone changes it, and that already reloads.
  refreshStatuses() {
    this.dotTargets.forEach(dot => {
      const block = this.blockById(Number(dot.dataset.scheduleId))
      if (!block || block.status === "snoozed" || block.status === "inactive") return

      const live = this.isLiveNow(block)
      dot.classList.toggle("is-live", live)
      dot.classList.toggle("is-active", !live)
      dot.title = live ? "Live right now" : "Active"
    })

    if (this.draft) this.renderModal()
  }

  // The venue's clock, not the browser's. Schedule times are venue-local wall
  // clock, so an operator in Toronto looking at a London venue must still see
  // London's idea of "now" -- the browser is only asked how much time has gone
  // by since the page was rendered.
  venueNow() {
    const clock = this.data_.clock
    const elapsed = Math.max(0, Date.now() - clock.epoch_ms)
    const total = clock.minutes + Math.floor(elapsed / 60000)
    const dayShift = Math.floor(total / MINUTES_IN_DAY)
    const index = WEEK.indexOf(clock.day)

    return {
      day: WEEK[(index + dayShift) % WEEK.length],
      yesterday: WEEK[(index + dayShift + WEEK.length - 1) % WEEK.length],
      minutes: total % MINUTES_IN_DAY,
    }
  }

  // Tomorrow where the venue is, which near midnight is not tomorrow where the
  // operator is.
  venueTomorrow() {
    const [year, month, day] = this.data_.clock.date.split("-").map(Number)
    return new Date(Date.UTC(year, month - 1, day) + 86400000).toISOString().slice(0, 10)
  }

  // The instant a date and time picked in the venue's zone actually falls on.
  venueInstant(dateString, timeString) {
    const [year, month, day] = String(dateString || "").split("-").map(Number)
    const [hours, minutes] = String(timeString || "09:00").split(":").map(Number)
    if (!year || !month || !day) return NaN

    return Date.UTC(year, month - 1, day, hours || 0, minutes || 0) - this.data_.clock.offset_minutes * 60000
  }

  isLiveNow(block) {
    if (!block.vs || !block.ve || !block.days.length) return false

    const now = this.venueNow()
    const start = this.mins(block.vs)
    const end = this.mins(block.ve)
    const overnight = this.isOvernight(block.vs, block.ve)

    if (block.days.indexOf(now.day) >= 0 && now.minutes >= start && (overnight || now.minutes <= end)) return true
    return overnight && block.days.indexOf(now.yesterday) >= 0 && now.minutes <= end
  }

  showToast(message) {
    this.toastTarget.textContent = message
    this.toastTarget.hidden = false
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => { this.toastTarget.hidden = true }, 2600)
  }

  // Which field the operator is in, so it can be handed back after the redraw.
  // The day groups are rebuilt wholesale on every change -- titles, pills and
  // the span bar all move as times are typed -- which replaces the very input
  // being typed into. Without this, editing a time throws you out of the field
  // after each segment.
  focusedField() {
    const element = document.activeElement
    if (!element || !element.dataset || !element.dataset.field || !element.dataset.key) return null

    return { key: element.dataset.key, field: element.dataset.field }
  }

  restoreFocus(field) {
    if (!field) return

    const selector = `[data-key="${field.key}"][data-field="${field.field}"]`
    const element = this.groupsTarget.querySelector(selector)
    if (element && element !== document.activeElement) element.focus()
  }

  renderModal() {
    if (!this.draft) return

    const focused = this.focusedField()
    const service = this.draft.service
    const menu = this.data_.menus.find(item => item.id === this.draft.menuId)
    const isAdd = this.draft.mode === "add"
    const type = this.data_.service_types.find(item => item.id === service.service_type_id)

    this.modalTarget.hidden = false
    this.modalMenuNameTarget.textContent = menu.name
    this.modalTitleTarget.textContent = isAdd ? "Add Service Type" : `Edit Schedule · ${type ? type.name : ""}`
    this.saveButtonTarget.textContent = isAdd ? "Add Service Type" : "Save Schedule"
    this.saveButtonTarget.disabled = false

    this.daysHeadingTarget.textContent = isAdd ? "2. Days and Times" : "Days and Times"
    this.floorsHeadingTarget.textContent = isAdd ? "3. Floors" : "Floors"
    this.availHeadingTarget.textContent = isAdd ? "4. Available On" : "Available On"
    this.payHeadingTarget.textContent = isAdd ? "5. Payment Type" : "Payment Type"

    this.renderTypes(menu, service, isAdd)
    this.renderGroups(service)
    this.renderFloors(service)
    this.renderChannels(service)
    this.renderPayments(service)

    const nights = service.schedules.filter(block => {
      const facts = this.facts(block)
      return facts.visOvernight || facts.ordOvernight
    }).length
    this.summaryTarget.textContent =
      `${service.schedules.length} ${service.schedules.length === 1 ? "schedule" : "schedules"}` +
      (nights ? ` · ${nights} crossing midnight` : "")

    this.errorTarget.hidden = !this.draft.error
    this.errorTarget.textContent = this.draft.error
    this.noticeTarget.hidden = !this.draft.notice
    this.noticeTarget.textContent = this.draft.notice

    this.restoreFocus(focused)
  }

  renderTypes(menu, service, isAdd) {
    this.typeSectionTarget.hidden = !isAdd
    if (!isAdd) return

    const used = menu.services.map(item => item.service_type_id).filter(id => id !== service.service_type_id)
    const available = this.data_.service_types.filter(type => used.indexOf(type.id) < 0)

    this.typeHeadingTarget.textContent = "1. Service Type"
    this.typeOptionsTarget.innerHTML = available.map(type => `
      <button type="button" class="menu-scheduling-choice ${type.id === service.service_type_id ? "is-on" : ""}"
        data-action="menu-scheduling#pickType" data-id="${type.id}">${this.escape(type.name)}</button>
    `).join("")

    this.typeHintTarget.textContent = available.length === 1
      ? `Only ${available[0].name} is available for this menu right now.`
      : "Each service type gets its own hours, floors and payment settings."
  }

  renderChannels(service) {
    this.channelOptionsTarget.innerHTML = [
      { field: "online", label: "Online Ordering", on: service.online },
      { field: "pos", label: "POS", on: service.pos },
    ].map(choice => `
      <button type="button" class="menu-scheduling-choice ${choice.on ? "is-on" : ""}"
        data-action="menu-scheduling#toggleChannel" data-field="${choice.field}">${choice.label}</button>
    `).join("")
  }

  renderPayments(service) {
    this.paymentOptionsTarget.innerHTML = this.data_.payment_methods.map(method => `
      <button type="button" class="menu-scheduling-choice ${service.payment_method_ids.indexOf(method.id) >= 0 ? "is-on" : ""}"
        data-action="menu-scheduling#pickPayment" data-id="${method.id}">${this.escape(method.name)}</button>
    `).join("")
  }

  renderFloors(service) {
    this.floorsSectionTarget.hidden = !this.data_.floors.length
    this.floorOptionsTarget.innerHTML = this.data_.floors.map(floor => `
      <button type="button" class="menu-scheduling-choice ${service.floor_ids.indexOf(floor.id) >= 0 ? "is-on" : ""}"
        data-action="menu-scheduling#toggleFloor" data-id="${floor.id}">${this.escape(floor.name)}, ${floor.tables} tables</button>
    `).join("")
  }

  // The groups are rebuilt on every change -- titles, pills and the span bar all
  // move as times are typed -- which replaces the very input being typed into.
  // Handing focus back to the replacement is not enough: an <input type="time">
  // holds the half-entered segment and the caret in the element itself, so the
  // swap loses the digits already keyed in and refocusing drops the caret back
  // on the hours. Detaching it even for an instant costs the same. So when the
  // operator is in a field, patch around it and leave that one element alone.
  renderGroups(service) {
    const html = service.schedules
      .map(block => (this.blockKey(block) === this.draft.editingId ? this.editingGroup(block) : this.readingGroup(block)))
      .join("")
    const live = this.liveField()

    if (!live) {
      this.groupsTarget.innerHTML = html
      return
    }

    const next = document.createElement("div")
    next.innerHTML = html
    this.patchAround(this.groupsTarget, next, live)
  }

  // The field the operator is in, as the element itself rather than a lookup.
  liveField() {
    const element = document.activeElement
    if (!element || !element.dataset || !element.dataset.field || !element.dataset.key) return null

    return this.groupsTarget.contains(element) ? element : null
  }

  // Swap current over to next child by child, descending into whichever child
  // holds the live element and never touching the element itself. Only the
  // branch the operator is typing in survives, so the rest still redraws.
  patchAround(current, next, live) {
    if (current === live) return

    const kids = Array.from(current.children)
    const fresh = Array.from(next.children)
    const index = kids.findIndex(child => child === live || child.contains(live))

    if (index < 0 || !fresh[index]) {
      current.replaceChildren(...fresh)
      return
    }

    this.patchAround(kids[index], fresh[index], live)

    for (let position = 0; position < Math.max(kids.length, fresh.length); position++) {
      if (position === index) continue

      if (kids[position] && fresh[position]) kids[position].replaceWith(fresh[position])
      else if (kids[position]) kids[position].remove()
      else current.appendChild(fresh[position])
    }
  }

  readingGroup(block) {
    const facts = this.facts(block)
    const key = this.blockKey(block)
    const warning = this.warningFor(block, facts)
    const info = !warning && facts.earlyBy > 0
      ? `Orders close ${this.durationText(facts.earlyBy)} before the menu closes`
      : ""
    const morning = this.nextMorning(block.days)

    return `
      <div class="menu-scheduling-group">
        <div class="menu-scheduling-group-rail"></div>
        <div class="menu-scheduling-group-body">
          <div class="menu-scheduling-group-head">
            <span class="menu-scheduling-group-title">${this.escape(this.groupTitle(block.days))}</span>
            <span class="menu-scheduling-group-days">${this.escape(block.days.length && block.days.length < 7 ? this.groupShort(block.days) : "")}</span>
            ${facts.visOvernight || facts.ordOvernight ? '<span class="menu-scheduling-pill is-midnight">☾ Crosses midnight</span>' : ""}
            ${this.statusPill(block)}
            <span class="menu-scheduling-spacer"></span>
            <button type="button" class="menu-scheduling-primary-btn is-small" data-action="menu-scheduling#editGroup" data-key="${key}">Edit</button>
            ${block.id ? `<button type="button" class="menu-scheduling-ghost-btn" data-action="menu-scheduling#openSnooze" data-schedule-id="${block.id}">${this.snoozeLabel(block)}</button>` : ""}
            <button type="button" class="menu-scheduling-link-btn is-danger" data-action="menu-scheduling#removeGroup" data-key="${key}">Remove</button>
          </div>

          <div class="menu-scheduling-group-rows">
            <div class="menu-scheduling-group-row">
              <span class="menu-scheduling-group-label">Menu visible</span>
              <span class="menu-scheduling-group-value">${this.escape(this.timeText(block.vs, block.ve))}</span>
              ${facts.visOvernight ? `<span class="menu-scheduling-pill is-night">☾ ${this.escape(block.ve)} · ${this.escape(morning)}</span>` : ""}
              <span class="menu-scheduling-group-note">open ${this.durationText(facts.visLen)}</span>
            </div>
            <div class="menu-scheduling-group-row">
              <span class="menu-scheduling-group-label">Ordering window</span>
              <span class="menu-scheduling-group-value">${this.escape(this.timeText(facts.os, facts.oe))}</span>
              ${facts.ordOvernight ? `<span class="menu-scheduling-pill is-night">☾ ${this.escape(facts.oe)} · ${this.escape(morning)}</span>` : ""}
              ${block.match ? '<span class="menu-scheduling-pill is-quiet">Same as viewing</span>' : ""}
              ${info ? `<span class="menu-scheduling-pill is-info">${this.escape(info)}</span>` : ""}
              ${warning ? `<span class="menu-scheduling-pill is-warn">${this.escape(warning)}</span>` : ""}
            </div>
          </div>

          ${this.timelineHtml(block, facts)}
        </div>
      </div>
    `
  }

  editingGroup(block) {
    const facts = this.facts(block)
    const key = this.blockKey(block)
    const morning = this.nextMorning(block.days)

    return `
      <div class="menu-scheduling-group is-editing">
        <div class="menu-scheduling-group-rail"></div>
        <div class="menu-scheduling-group-body">
          <div class="menu-scheduling-group-head">
            <span class="menu-scheduling-group-title">${this.escape(this.groupTitle(block.days))}</span>
            <span class="menu-scheduling-pill is-editing">Editing</span>
            ${facts.visOvernight || facts.ordOvernight ? '<span class="menu-scheduling-pill is-midnight">☾ Crosses midnight</span>' : ""}
            <span class="menu-scheduling-spacer"></span>
            <button type="button" class="menu-scheduling-link-btn is-danger" data-action="menu-scheduling#removeGroup" data-key="${key}">Remove</button>
          </div>

          <div class="menu-scheduling-day-picker">
            <button type="button" class="menu-scheduling-chip-btn" data-action="menu-scheduling#everyDay" data-key="${key}">Every day</button>
            <button type="button" class="menu-scheduling-chip-btn" data-action="menu-scheduling#clearDays" data-key="${key}">Clear</button>
            <span class="menu-scheduling-divider"></span>
            ${WEEK.map(day => `
              <button type="button" class="menu-scheduling-chip-btn ${block.days.indexOf(day) >= 0 ? "is-on" : ""}"
                data-action="menu-scheduling#toggleDay" data-key="${key}" data-day="${day}">${day.slice(0, 3)}</button>
            `).join("")}
          </div>
          <p class="menu-scheduling-hint">${this.escape(
            facts.visOvernight && block.days.length
              ? `One night, one block: ${this.groupShort(block.days)} nights run into ${morning}.`
              : "Pick every day that shares these hours."
          )}</p>

          <div class="menu-scheduling-opening-strip">
            <span>${this.escape(this.openingText(block.days))}</span>
            <span class="menu-scheduling-spacer"></span>
            ${this.matchesOpening(block) ? '<span class="menu-scheduling-matches">✓ Matches opening hours</span>' : ""}
            <button type="button" class="menu-scheduling-outline-btn" data-action="menu-scheduling#useOpeningHours" data-key="${key}">Use opening hours</button>
          </div>

          <div class="menu-scheduling-fields">
            <div class="menu-scheduling-field-row">
              <span class="menu-scheduling-field-label">Menu visible</span>
              <input type="time" step="300" value="${block.vs}" data-action="menu-scheduling#setTime" data-key="${key}" data-field="vs">
              <span class="menu-scheduling-field-sep">until</span>
              <input type="time" step="300" value="${block.ve}" data-action="menu-scheduling#setTime" data-key="${key}" data-field="ve">
              ${facts.visOvernight ? `<span class="menu-scheduling-field-note">ends ${this.escape(block.ve)} next day · ${this.escape(morning)}</span>` : ""}
            </div>

            <label class="menu-scheduling-match">
              <input type="checkbox" ${block.match ? "checked" : ""} data-action="menu-scheduling#toggleMatch" data-key="${key}">
              <span>Match viewing hours</span>
            </label>

            <div class="menu-scheduling-field-row ${block.match ? "is-mirrored" : ""}">
              <span class="menu-scheduling-field-label">Ordering window</span>
              <input type="time" step="300" value="${facts.os || ""}" ${block.match ? "readonly" : ""}
                data-action="menu-scheduling#setTime" data-key="${key}" data-field="os">
              <span class="menu-scheduling-field-sep">until</span>
              <input type="time" step="300" value="${facts.oe || ""}" ${block.match ? "readonly" : ""}
                data-action="menu-scheduling#setTime" data-key="${key}" data-field="oe">
              ${facts.ordOvernight ? `<span class="menu-scheduling-field-note">ends ${this.escape(facts.oe)} next day · ${this.escape(morning)}</span>` : ""}
            </div>
          </div>

          <div class="menu-scheduling-group-actions">
            <button type="button" class="menu-scheduling-ghost-btn" data-action="menu-scheduling#cancelGroup" data-key="${key}">Cancel</button>
            <button type="button" class="menu-scheduling-primary-btn is-small" data-action="menu-scheduling#doneGroup" data-key="${key}">Done</button>
          </div>
        </div>
      </div>
    `
  }

  timelineHtml(block, facts) {
    if (!block.days.length || !block.vs || !block.ve) return ""

    const total = Math.max(facts.visLen, 1)
    const left = Math.min(Math.max((facts.offset / total) * 100, 0), 100)
    const width = Math.min(Math.max((facts.ordLen / total) * 100, 1.5), Math.max(100 - left, 1.5))
    const midnight = ((MINUTES_IN_DAY - this.mins(block.vs)) / total) * 100

    return `
      <div class="menu-scheduling-timeline">
        <div class="menu-scheduling-timeline-track">
          <div class="menu-scheduling-timeline-order" style="left:${left}%; width:${width}%"></div>
          ${facts.visOvernight ? `<div class="menu-scheduling-timeline-midnight" style="left:${midnight}%"></div>` : ""}
        </div>
        <div class="menu-scheduling-timeline-labels">
          <span class="menu-scheduling-timeline-start">${this.escape(block.vs)}</span>
          ${facts.visOvernight ? `<span class="menu-scheduling-timeline-mid" style="left:${midnight}%">midnight</span>` : ""}
          <span class="menu-scheduling-timeline-end">${this.escape(block.ve)}${facts.visOvernight ? " next day" : ""}</span>
        </div>
      </div>
    `
  }

  // A snoozed block names its return time here, not just its state -- knowing it
  // comes back on Wednesday is the point.
  statusPill(block) {
    switch (block.status) {
      case "snoozed":
        return `<span class="menu-scheduling-pill is-snoozed">${this.escape(block.snoozed_until_label || "Snoozed")}</span>`
      case "inactive": return '<span class="menu-scheduling-pill is-inactive">Not active</span>'
      case "live": return '<span class="menu-scheduling-pill is-live">Live right now</span>'
      default: return ""
    }
  }

  snoozeLabel(block) {
    return this.labelForStatus(block.status)
  }

  renderSnooze() {
    const snooze = this.snooze
    this.snoozeModalTarget.hidden = false

    this.snoozeTitleTarget.textContent = snooze.status === "snoozed"
      ? "Un-Snooze Schedule"
      : (snooze.status === "inactive" ? "Reactivate Schedule" : "Snooze Schedule")
    this.snoozeSubjectTarget.textContent = snooze.subject

    this.snoozeOptionsTarget.hidden = snooze.view !== "options"
    this.snoozeCustomTarget.hidden = snooze.view !== "custom"
    this.snoozeConfirmTarget.hidden = snooze.view !== "confirm"

    this.snoozeDateTarget.value = snooze.date
    this.snoozeTimeTarget.value = snooze.time
    this.snoozeErrorTarget.hidden = !snooze.error
    this.snoozeErrorTarget.textContent = snooze.error

    this.snoozeQuestionTarget.textContent = snooze.status === "snoozed"
      ? "Are you sure you want to un-snooze this schedule? It will start serving again straight away."
      : "Reactivate this schedule? It will start serving again on its next scheduled day."
    this.snoozeConfirmButtonTarget.textContent = snooze.status === "snoozed" ? "Yes, un-snooze" : "Yes, reactivate"
  }

  // Absent whenever forgery protection is off, so read it defensively rather
  // than letting the whole request die on a null.
  csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ""
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
